import { useLanguage } from "@tlc/ls2pac/components/Language";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
import React from "react";
import PrintDialog from "../PrintDialog";
import { HoldingsInformation } from "@tlc/ls2pac/dto/HoldingsInformation";
import { useAvailability } from "../../../../contexts/AvailabilityContext";
import { useAccount } from "@tlc/ls2pac/components/Account";
import { ItemAvailability } from "../../resource/ItemAvailability";
import { ListService, ListType } from "../ListService";
import { ListSortType } from "./SavedListSortControl";

interface SavedListPrintDialogProps {
  listId: number;
  listType: ListType;
  sort: ListSortType;
  total: number;
  listName: string;
  disabled?: boolean;
}
interface PrintResource extends ResourceDWR {
  collectionName: string;
  callNumber: string;
}

function getCallNumberFromHolding(holding: HoldingsInformation): string {
  if (!holding?.shelfLocation) return "";
  return holding.shelfLocation.trim();
}

function getPrintResources(
  resourceList: ResourceDWR[],
  issuingBranchId: string,
  availability: (ItemAvailability | null)[],
): PrintResource[] {
  let availabilityIndex = 0;

  const resources = resourceList
    .filter((r) => r.rtype === 1)
    .map((resource) => {
      const { holdingsInformations } = resource;

      // if the resource only has one holding print the collection and call number for the single holding
      if (holdingsInformations.length <= 1) {
        return {
          ...resource,
          collectionName: holdingsInformations[0]?.collectionName ?? "",
          callNumber: getCallNumberFromHolding(holdingsInformations[0]),
        };
      }

      // get local holdings based on patron issuing branch
      const localHoldings = holdingsInformations.filter(
        (h) => h.branchIdentifier === issuingBranchId,
      );

      const holdingsToCheck =
        localHoldings.length > 0 ? localHoldings : holdingsInformations;

      const holdingAvailability = availability.slice(
        availabilityIndex,
        availabilityIndex + holdingsToCheck.length,
      );

      availabilityIndex += holdingsToCheck.length;

      // select the first available holding
      // if none are available, fall back to the first non-circulating
      // if none are available or non-circulating, fall back to the first from the selection
      const holdingForOnShelf =
        holdingsToCheck.find((_, i) => holdingAvailability[i].available) ??
        holdingsToCheck.find(
          (_, i) => !holdingAvailability[i].nonCirculating,
        ) ??
        holdingsToCheck[0];

      // TODO refactor to use shared logic with the ShelfLocation component (abstract to a hook)
      return {
        ...resource,
        collectionName: holdingForOnShelf.collectionName ?? "",
        callNumber: getCallNumberFromHolding(holdingForOnShelf),
      };
    });

  return resources;
}

export default function SavedListPrintDialog({
  listId,
  listType,
  sort,
  total,
  listName,
  disabled = false,
}: SavedListPrintDialogProps) {
  const { translate } = useLanguage();
  const { patron } = useAccount();
  const { getAvailabilityForBarcodesAndResourceIds } = useAvailability();
  const [printResources, setPrintResources] = React.useState<PrintResource[]>(
    [],
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasFetchedAllResources, setHasFetchedAllResources] =
    React.useState(false);

  const formatWithPeriod = (str: string): string => {
    const trimmed = (str || "").trim();
    if (trimmed.length === 0) return "";
    return trimmed.match(/\.$/) ? trimmed : trimmed + ".";
  };

  // Reset fetch state if user changes sort order, switches lists, or removes an item from the list
  React.useEffect(() => {
    setHasFetchedAllResources(false);
    setPrintResources([]);
  }, [listId, sort, total]);

  const handleFetchForPrint = async () => {
    if (hasFetchedAllResources || total === 0) return;
    setIsLoading(true);

    try {
      const { resources } = await ListService.getAllListResources(
        listId,
        listType,
        total,
        sort,
      );

      const requests = resources
        .filter((r) => r.rtype === 1 && r.holdingsInformations.length > 1)
        .flatMap((r) =>
          r.holdingsInformations.map((h) => ({
            resourceId: r.id,
            barcode: h.barcode,
          })),
        );

      const availability = await getAvailabilityForBarcodesAndResourceIds(
        requests,
      );
      const computed = getPrintResources(
        resources,
        patron?.issuingBranchId,
        availability,
      );

      setPrintResources(computed);

      setHasFetchedAllResources(true);
    } catch (error) {
      console.error("Failed to fetch resources for printing:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const buildHtml = () => {
    let html = `
      <h2 align="center"><b>${translate("savedLists")}</b></h2>
      <h3>${listName}</h3>
      <ul style="list-style: none; padding-left: 20px;">
    `;
    if (printResources.length > 0) {
      printResources.forEach((resource) => {
        const shortAuthor = formatWithPeriod(resource.shortAuthor ?? "");
        const shortTitle = formatWithPeriod(resource.shortTitle ?? "");
        html += `
          <li style="padding-bottom: 10px">
            <u>${shortTitle}</u> ${shortAuthor} 
            ${translate("collection")}: ${resource.collectionName ?? ""}, 
            ${translate("callNumber")}: ${resource.callNumber ?? ""}
          </li>
        `;
      });
    } else {
      html += `<li>${translate("noItemsToPrint")}</li>`;
    }
    html += "</ul>";
    return html;
  };

  return (
    <PrintDialog
      buttonText={translate("printList")}
      dialogTitle={translate("savedLists")}
      htmlContent={buildHtml()}
      isLoading={isLoading}
      disabled={disabled}
      onOpenButtonClick={handleFetchForPrint}
    />
  );
}
